import { CheckCircle, Key, Loader2, Shield, ShieldCheck, XCircle } from "lucide-react";
import { useState } from "react";

import { ApiKeysDialog } from "./ApiKeysDialog";
import { LicensesDialog } from "@/features/licenses";
import { UserInstallationsDialog } from "./UserInstallationsDialog";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useAssignPassword,
  useResendVerification,
  useSendPasswordResetEmail,
  useUpdateUserStatus,
} from "@/features/users/hooks/useUsers";
import type { User } from "@/features/users/schemas/user";
import { getUserFullName } from "@/features/users/utils/user-utils";

export type UserAction =
  | "reset-password-email"
  | "reset-password-direct"
  | "verify-email"
  | "verify-direct"
  | "enable"
  | "disable"
  | "api-keys"
  | "licenses"
  | "installations";

interface UserActionsDialogProps {
  user: User | null;
  action: UserAction | null;
  open: boolean;
  onClose: () => void;
}

interface ResultState {
  type: "success" | "error";
  message: string;
  generatedPassword?: string;
}

const ACTION_META: Record<
  UserAction,
  {
    title: string;
    description: (name: string, email: string) => string;
    confirmLabel: string;
    variant: "default" | "destructive";
  }
> = {
  "reset-password-email": {
    title: "Restablecer contraseña por correo",
    description: (_name, email) =>
      `Se enviará un enlace de restablecimiento de contraseña a ${email}.`,
    confirmLabel: "Enviar correo",
    variant: "default",
  },
  "reset-password-direct": {
    title: "Generar nueva contraseña",
    description: (name) =>
      `Se generará y asignará una contraseña aleatoria para ${name}. Deberás comunicarla manualmente al usuario.`,
    confirmLabel: "Generar contraseña",
    variant: "default",
  },
  "verify-email": {
    title: "Enviar verificación por correo",
    description: (_name, email) =>
      `Se reenviará el correo de verificación de cuenta a ${email}.`,
    confirmLabel: "Enviar correo",
    variant: "default",
  },
  "verify-direct": {
    title: "Verificar cuenta directamente",
    description: (name) =>
      `Se verificará y habilitará la cuenta de ${name} de forma inmediata, sin enviar ningún correo.`,
    confirmLabel: "Verificar ahora",
    variant: "default",
  },
  enable: {
    title: "Habilitar usuario",
    description: (name) =>
      `Se habilitará la cuenta de ${name} para que pueda iniciar sesión.`,
    confirmLabel: "Habilitar",
    variant: "default",
  },
  disable: {
    title: "Deshabilitar usuario",
    description: (name) =>
      `Se deshabilitará la cuenta de ${name}. No podrá iniciar sesión.`,
    confirmLabel: "Deshabilitar",
    variant: "destructive",
  },
  "api-keys": {
    title: "Gestionar API Keys",
    description: (name) => `Gestiona las API Keys de ${name}.`,
    confirmLabel: "Continuar",
    variant: "default",
  },
  licenses: {
    title: "Gestionar Licencias",
    description: (name) => `Gestiona las licencias de ${name}.`,
    confirmLabel: "Continuar",
    variant: "default",
  },
  installations: {
    title: "Descargar Instalaciones",
    description: (name) => `Descarga las instalaciones preconfiguradas para ${name}.`,
    confirmLabel: "Continuar",
    variant: "default",
  },
};

export function UserActionsDialog({
  user,
  action,
  open,
  onClose,
}: UserActionsDialogProps) {
  const [result, setResult] = useState<ResultState | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const assignPasswordMutation = useAssignPassword();
  const updateStatusMutation = useUpdateUserStatus();
  const sendResetEmailMutation = useSendPasswordResetEmail();
  const resendVerificationMutation = useResendVerification();

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!user || !action) return;

    setIsLoading(true);
    setResult(null);

    try {
      switch (action) {
        case "reset-password-email": {
          await sendResetEmailMutation.mutateAsync(user.email);
          setResult({
            type: "success",
            message: `Correo de restablecimiento enviado a ${user.email}.`,
          });
          break;
        }
        case "reset-password-direct": {
          const data = await assignPasswordMutation.mutateAsync(user.uid);
          setResult({
            type: "success",
            message: "Contraseña generada. Cópiala antes de cerrar.",
            generatedPassword: data.password,
          });
          break;
        }
        case "verify-email": {
          const response = await resendVerificationMutation.mutateAsync(user.email);
          if (response.sent) {
            setResult({
              type: "success",
              message: `Correo de verificación enviado a ${user.email}.`,
            });
          } else if (response.reason === "already_verified") {
            setResult({
              type: "success",
              message: "El usuario ya está verificado.",
            });
          } else {
            setResult({
              type: "success",
              message: "El correo de verificación no se pudo enviar.",
            });
          }
          break;
        }
        case "verify-direct": {
          await updateStatusMutation.mutateAsync({ uid: user.uid, action: "verify" });
          setResult({
            type: "success",
            message: "Cuenta verificada y habilitada correctamente.",
          });
          break;
        }
        case "enable": {
          await updateStatusMutation.mutateAsync({ uid: user.uid, action: "enable" });
          setResult({ type: "success", message: "Cuenta habilitada correctamente." });
          break;
        }
        case "disable": {
          await updateStatusMutation.mutateAsync({ uid: user.uid, action: "disable" });
          setResult({ type: "success", message: "Cuenta deshabilitada correctamente." });
          break;
        }
      }
    } catch (error) {
      setResult({
        type: "error",
        message: error instanceof Error ? error.message : "Ocurrió un error inesperado.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user || !action) return null;
  const userName = getUserFullName(user);

  if (action === "api-keys") {
    return (
      <ApiKeysDialog
        userId={user.uid}
        userName={userName}
        open={open}
        onClose={onClose}
      />
    );
  }

  if (action === "licenses") {
    return (
      <LicensesDialog
        userId={user.uid}
        userName={userName}
        open={open}
        onClose={onClose}
      />
    );
  }

  if (action === "installations") {
    return <UserInstallationsDialog user={user} open={open} onClose={onClose} />;
  }

  const meta = ACTION_META[action];

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {action.startsWith("reset-password") && (
                <Key className="h-5 w-5 text-amber-500" />
              )}
              {action.startsWith("verify") && (
                <ShieldCheck className="h-5 w-5 text-green-500" />
              )}
              {action === "enable" && <Shield className="h-5 w-5 text-blue-500" />}
              {action === "disable" && <Shield className="h-5 w-5 text-red-500" />}
              {meta.title}
            </DialogTitle>
            <DialogDescription>
              {meta.description(userName, user.email)}
            </DialogDescription>
          </DialogHeader>

          {/* Resultado */}
          {result && (
            <div
              className={`rounded-lg p-4 ${
                result.type === "success"
                  ? "bg-green-50 border border-green-200"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              <div className="flex items-start gap-2">
                {result.type === "success" ? (
                  <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p
                    className={`text-sm font-medium ${
                      result.type === "success" ? "text-green-800" : "text-red-800"
                    }`}
                  >
                    {result.message}
                  </p>
                  {/* Contraseña generada con botón copiar */}
                  {result.generatedPassword && (
                    <div className="mt-3">
                      <p className="text-xs text-green-600 mb-1">Contraseña generada:</p>
                      <div className="flex items-center gap-2">
                        <code className="bg-white border border-green-300 rounded px-3 py-2 text-base font-mono font-bold text-green-900 tracking-widest select-all flex-1">
                          {result.generatedPassword}
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            navigator.clipboard.writeText(result.generatedPassword!)
                          }
                        >
                          Copiar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleClose} disabled={isLoading}>
              {result ? "Cerrar" : "Cancelar"}
            </Button>
            {!result && (
              <Button variant={meta.variant} onClick={handleConfirm} disabled={isLoading}>
                {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {meta.confirmLabel}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
